import { useLayoutEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '../../store';
import { HEAT_HEX, HEAT_ICON, type HeatBucket } from '../../lib/health';
import { mapContextOf } from '../map/mapContext';
import { inEditMode } from '../../lib/mode';

/**
 * The clearance heatmap's legend (V0.39) — a real React region over its own host, NOT the
 * imperative `#aidoc-toolbar` pattern: that one survives only because it was ported under a
 * baseline freeze, and there is no baseline to protect here.
 *
 * THE HOST IS A SIBLING OF `#stage`, deliberately. The stage owns the map's pointer handling,
 * and a legend rendered inside it would start a pan on every click. Position comes from the
 * stylesheet off `--stage-left` — the one CSS variable `applySidebar()` writes — so the legend
 * slides with the sidebar without a second element lookup anywhere.
 *
 * The counts come from the SAME `HeatIndex` the nodes paint from (`mapContextOf`'s per-publish
 * build), so the legend and the map cannot disagree; the selector returns primitives only, so
 * `useShallow` re-renders this exactly when a tally or the mode actually moves. Hidden
 * entirely — unmounted, not display:none'd from JS — while the mode is off, which is what
 * keeps every existing baseline free of it.
 */

const ROWS: readonly { key: HeatBucket; word: string; title: string }[] = [
  { key: 'blue', word: 'Unplanned', title: 'No documentation plan yet — nobody has said what this node owes' },
  { key: 'red', word: 'Missing', title: 'The plan names documentation that is not attached' },
  {
    key: 'amber',
    word: 'Uncleared',
    title: 'Documentation attached, but not every page is approved in this language',
  },
  {
    key: 'green',
    word: 'Cleared',
    title: 'Everything expected is attached and every page is approved',
  },
  { key: 'slate', word: 'Exempt', title: 'Nothing expected here — stated in the plan' },
];

export function HeatmapLegend({ host }: { host: HTMLElement }) {
  const v = useAppStore(
    useShallow((s) => {
      const { counts } = mapContextOf(s).heat;
      return {
        on: s.heatmapOn && inEditMode(s),
        blue: counts.blue,
        red: counts.red,
        amber: counts.amber,
        green: counts.green,
        slate: counts.slate,
      };
    }),
  );

  useLayoutEffect(() => {
    host.classList.toggle('show', v.on);
  }, [v.on, host]);

  if (!v.on) return null;

  return (
    <div className="heatlegend" role="note" aria-label="Clearance heatmap legend">
      <span className="heatlegend-title">Clearance</span>
      {ROWS.map((r) => (
        <span className="heatlegend-row" key={r.key} data-heat={r.key} title={r.title}>
          <span className="heatlegend-dot" style={{ background: HEAT_HEX[r.key] }}>
            {HEAT_ICON[r.key] ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <use href={`#${HEAT_ICON[r.key]}`} />
              </svg>
            ) : null}
          </span>
          <span className="heatlegend-word">{r.word}</span>
          <span className="heatlegend-count">{v[r.key]}</span>
        </span>
      ))}
    </div>
  );
}
